import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Calendar,
  Clock,
  Video,
  Coffee,
  User,
  Mail,
  Phone,
  MessageSquare,
  Check,
  Star,
} from "lucide-react";

const Booking: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: "",
    meetingType: "online",
    selectedDate: null as Date | null,
    selectedTime: "",
    timezone: "SAST",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Function to handle meeting type selection and smooth scroll
  const handleMeetingTypeSelect = (meetingType: string) => {
    setFormData((prev) => ({
      ...prev,
      meetingType: meetingType,
    }));

    // Smooth scroll to the booking form
    const bookingForm = document.getElementById("booking-form");
    if (bookingForm) {
      bookingForm.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const projectTypes = [
    "Website Development",
    "Mobile App",
    "Maintenance & Support",
    "Consultation",
    "Other",
  ];

  const timezoneOptions = [
    { value: "SAST", label: "SAST (South African Standard Time)" },
    { value: "EST", label: "EST (Eastern Standard Time)" },
    { value: "GMT", label: "GMT (Greenwich Mean Time)" },
  ];

  // Business hours configuration
  const businessHours = {
    SAST: {
      weekdays: { start: "19:00", end: "22:00" }, // 7:00 PM - 10:00 PM
      weekends: { start: "08:00", end: "22:00" }, // 8:00 AM - 10:00 PM
    },
    EST: {
      weekdays: { start: "12:00", end: "15:00" }, // 12:00 PM - 3:00 PM
      weekends: { start: "01:00", end: "15:00" }, // 1:00 AM - 3:00 PM
    },
    GMT: {
      weekdays: { start: "17:00", end: "20:00" }, // 5:00 PM - 8:00 PM
      weekends: { start: "06:00", end: "20:00" }, // 6:00 AM - 8:00 PM
    },
  };

  // Generate time slots based on timezone and selected date
  const getAvailableTimeSlots = () => {
    if (!formData.selectedDate || !formData.timezone) return [];

    const selectedDay = formData.selectedDate.getDay();
    const isWeekend = selectedDay === 0 || selectedDay === 6; // Sunday = 0, Saturday = 6
    const hours =
      businessHours[formData.timezone as keyof typeof businessHours];
    const { start, end } = isWeekend ? hours.weekends : hours.weekdays;

    const startHour = parseInt(start.split(":")[0]);
    const startMinute = parseInt(start.split(":")[1]);
    const endHour = parseInt(end.split(":")[0]);
    const endMinute = parseInt(end.split(":")[1]);

    const timeSlots = [];
    let currentHour = startHour;
    let currentMinute = startMinute;

    while (
      currentHour < endHour ||
      (currentHour === endHour && currentMinute <= endMinute)
    ) {
      const timeString = `${currentHour.toString().padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}`;
      timeSlots.push(timeString);

      // Add 30 minutes
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour += 1;
      }
    }

    return timeSlots;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // Reset selected time if timezone changes
      if (name === "timezone") {
        newData.selectedTime = "";
      }

      return newData;
    });
  };

  const handleDateChange = (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      selectedDate: date,
      selectedTime: "", // Reset selected time when date changes
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    // Set the flag so BookingSuccess knows the form was submitted
    sessionStorage.setItem("bookingFormSubmitted", "true");
    setIsSubmitting(true);
    // Do NOT prevent default, let Netlify handle the POST and redirect
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.projectType &&
      formData.selectedDate &&
      formData.selectedTime &&
      formData.timezone
    );
  };

  // Filter out past dates
  const filterDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  // Allow all days including weekends (we have weekend operating hours)
  const filterWeekdays = (date: Date) => {
    return true; // Allow all days since we have weekend operating hours
  };

  return (
    <div>
      {/* Header Section */}
      <section
        className="hero"
        style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
      >
        <div className="hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>
              Book a <span className="gradient-text">Consultation</span>
            </h1>
            <p>
              Schedule a meeting to discuss your project and explore
              possibilities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Options */}
      <section className="section" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              Choose Your <span className="gradient-text">Meeting Type</span>
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "2rem",
                marginBottom: "3rem",
              }}
            >
              <div
                className="card"
                style={{
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onClick={() => handleMeetingTypeSelect("online")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(156, 2, 203, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "";
                }}
              >
                <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  <Video size={48} />
                </div>
                <h3>Online Meeting</h3>
                <p>
                  Virtual consultation via video call. Perfect for initial
                  discussions and planning sessions.
                </p>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "1rem 0",
                    color: "#666",
                  }}
                >
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    Screen sharing capabilities
                  </li>
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    Record session for reference
                  </li>
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    No travel required
                  </li>
                </ul>
              </div>

              <div
                className="card"
                style={{
                  textAlign: "center",
                  border: "2px solid #9C02CB",
                  cursor: "pointer",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onClick={() => handleMeetingTypeSelect("in-person")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(156, 2, 203, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "";
                }}
              >
                <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  <Coffee size={48} />
                </div>
                <h3>In-Person Meeting</h3>
                <p>
                  Face-to-face consultation at a coffee shop. Includes
                  complimentary coffee and snacks!
                </p>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "1rem 0",
                    color: "#666",
                  }}
                >
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    Free coffee & snacks
                  </li>
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    Personal connection
                  </li>
                  <li
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Check size={16} color="#22c55e" />
                    Relaxed atmosphere
                  </li>
                </ul>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #9C02CB 0%, #d946ef 100%)",
                    color: "white",
                    padding: "0.5rem 1rem",
                    borderRadius: "20px",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    display: "inline-block",
                    marginTop: "1rem",
                  }}
                >
                  <Star size={16} style={{ marginRight: "0.5rem" }} />
                  Popular Choice
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section
        className="section"
        style={{ background: "#f9fafb" }}
        id="booking-form"
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              Book Your <span className="gradient-text">Consultation</span>
            </h2>

            {isSubmitted ? (
              <div
                className="success-message"
                style={{ maxWidth: "600px", margin: "0 auto" }}
              >
                <h3 style={{ color: "#065f46", marginBottom: "1rem" }}>
                  <Check style={{ marginRight: "0.5rem" }} />
                  Booking Confirmed!
                </h3>
                <p>
                  Thank you for booking a consultation with OS. We'll send you a
                  confirmation email shortly with all the details.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="form-button"
                  style={{
                    marginTop: "1rem",
                    width: "auto",
                    padding: "0.75rem 1.5rem",
                  }}
                >
                  Book Another Meeting
                </button>
              </div>
            ) : (
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                action="/booking-success"
                className="form-container"
                onSubmit={handleSubmit}
              >
                {/* Netlify hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                {/* Personal Information */}
                <div style={{ marginBottom: "2rem" }}>
                  <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                    Personal Information
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: "1rem",
                    }}
                  >
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        <User size={16} style={{ marginRight: "0.5rem" }} />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email" className="form-label">
                        <Mail size={16} style={{ marginRight: "0.5rem" }} />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: "1rem",
                    }}
                  >
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">
                        <Phone size={16} style={{ marginRight: "0.5rem" }} />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company" className="form-label">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Information */}
                <div style={{ marginBottom: "2rem" }}>
                  <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                    Project Information
                  </h3>
                  <div className="form-group">
                    <label htmlFor="projectType" className="form-label">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">
                      <MessageSquare
                        size={16}
                        style={{ marginRight: "0.5rem" }}
                      />
                      Project Description
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="form-textarea"
                      placeholder="Please describe your project, goals, and any specific requirements..."
                    />
                  </div>
                </div>

                {/* Meeting Preferences */}
                <div style={{ marginBottom: "2rem" }}>
                  <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                    Meeting Preferences
                  </h3>
                  <div className="form-group">
                    <label className="form-label">Meeting Type</label>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name="meetingType"
                          value="online"
                          checked={formData.meetingType === "online"}
                          onChange={handleInputChange}
                        />
                        <Video size={16} />
                        Online Meeting
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name="meetingType"
                          value="in-person"
                          checked={formData.meetingType === "in-person"}
                          onChange={handleInputChange}
                        />
                        <Coffee size={16} />
                        In-Person Meeting
                      </label>
                    </div>
                  </div>
                </div>

                {/* Date and Time Selection */}
                <div style={{ marginBottom: "2rem" }}>
                  <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                    Select Date & Time
                  </h3>

                  {/* Timezone Selection */}
                  <div
                    className="form-group"
                    style={{ marginBottom: "1.5rem" }}
                  >
                    <label htmlFor="timezone" className="form-label">
                      <Clock size={16} style={{ marginRight: "0.5rem" }} />
                      Timezone *
                    </label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      {timezoneOptions.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>

                    {/* Business Hours Info */}
                    <div
                      style={{
                        marginTop: "0.5rem",
                        padding: "0.75rem",
                        background: "#f3f4f6",
                        borderRadius: "6px",
                        fontSize: "0.875rem",
                        color: "#6b7280",
                      }}
                    >
                      <strong>Business Hours ({formData.timezone}):</strong>
                      <br />
                      {formData.timezone === "SAST" && (
                        <>
                          <div>• Monday-Friday: 7:00 PM - 10:00 PM</div>
                          <div>• Saturday-Sunday: 8:00 AM - 10:00 PM</div>
                        </>
                      )}
                      {formData.timezone === "EST" && (
                        <>
                          <div>• Monday-Friday: 12:00 PM - 3:00 PM</div>
                          <div>• Saturday-Sunday: 1:00 AM - 3:00 PM</div>
                        </>
                      )}
                      {formData.timezone === "GMT" && (
                        <>
                          <div>• Monday-Friday: 5:00 PM - 8:00 PM</div>
                          <div>• Saturday-Sunday: 6:00 AM - 8:00 PM</div>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: "2rem",
                    }}
                  >
                    <div className="form-group">
                      <label className="form-label">
                        <Calendar size={16} style={{ marginRight: "0.5rem" }} />
                        Preferred Date *
                      </label>
                      <div
                        className="booking-calendar"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          overflow: "hidden",
                          maxWidth: "100%",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "1rem",
                          background: "#fff",
                        }}
                      >
                        <DatePicker
                          selected={formData.selectedDate}
                          onChange={handleDateChange}
                          filterDate={(date) =>
                            filterDate(date) && filterWeekdays(date)
                          }
                          inline
                          minDate={new Date()}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="selectedTime" className="form-label">
                        <Clock size={16} style={{ marginRight: "0.5rem" }} />
                        Preferred Time *
                      </label>
                      <select
                        id="selectedTime"
                        name="selectedTime"
                        value={formData.selectedTime}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                        disabled={!formData.selectedDate}
                      >
                        <option value="">
                          {!formData.selectedDate
                            ? "Please select a date first"
                            : "Select preferred time"}
                        </option>
                        {getAvailableTimeSlots().map((time: string) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                      {formData.selectedDate &&
                        getAvailableTimeSlots().length === 0 && (
                          <div
                            style={{
                              marginTop: "0.5rem",
                              color: "#dc2626",
                              fontSize: "0.875rem",
                            }}
                          >
                            No available time slots for this date in{" "}
                            {formData.timezone} timezone.
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="form-button"
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="loading-spinner"
                        style={{ marginRight: "0.5rem" }}
                      ></span>
                      Booking...
                    </>
                  ) : (
                    <>
                      <Calendar size={20} style={{ marginRight: "0.5rem" }} />
                      Book Consultation
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              What to <span className="gradient-text">Expect</span>
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "2rem",
              }}
            >
              <div className="card" style={{ textAlign: "center" }}>
                <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  <MessageSquare size={32} />
                </div>
                <h3>Project Discussion</h3>
                <p>
                  We'll discuss your project requirements, goals, and vision in
                  detail.
                </p>
              </div>
              <div className="card" style={{ textAlign: "center" }}>
                <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  <Clock size={32} />
                </div>
                <h3>Timeline Planning</h3>
                <p>
                  We'll create a realistic timeline and milestone plan for your
                  project.
                </p>
              </div>
              <div className="card" style={{ textAlign: "center" }}>
                <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  <Star size={32} />
                </div>
                <h3>Custom Proposal</h3>
                <p>
                  You'll receive a detailed proposal tailored to your specific
                  needs.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
